// src/components/core/TableDataExplorer.tsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { limitsConfig } from "@/config";
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import { useDebounce } from "@/lib";
import { downloadQueryToCsv } from "@/lib/duckdb/download";
import { applyLimit } from "@/lib/utils/sql";
import DataTable from "@/core/data-tables/DataTable";
import { useQueryRunner } from "@/core/query/useQueryRunner";
import { useFilters } from "@/core/data-tables/useFilters";
import { useTableSchema } from "@/core/schema/useTableSchema";
import { FiltersPanel } from "@/core/data-tables/filters/FiltersPanel";
import { Pager } from "@/core/data-tables/ui/Pager";
import { useDisplayOptions } from "@/core/data-tables/useDisplayOptions";
import { DisplayOptions } from "@/core/data-tables/DisplayOptions";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@ui/card";
import { Button } from "@ui/button";
import { Input } from "@ui/input";
import {
  Filter as FilterIcon,
  Download as DownloadIcon,
} from "lucide-react";

interface Props {
  conn: AsyncDuckDBConnection | null;
  tables: string[];
  setMsg: (msg: string) => void;
}

const { pageSize: defaultPageSize, rowDisplayOptions } =
  limitsConfig.tableExplorer;

export default function TableDataExplorer({
  conn,
  tables,
  setMsg,
}: Props) {
  const [table, setTable] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultPageSize);
  const [showFilterPanel, setShowFilterPanel] = useState(true);
  const [csvName, setCsvName] = useState("");

  /* ── stable ref for toast fn ── */
  const msgRef = useRef(setMsg);
  useEffect(() => {
    msgRef.current = setMsg;
  }, [setMsg]);

  /* display options */
  const {
    open: optsOpen,
    toggle: toggleOpts,
    improveNames,
    setImproveNames,
    scheme,
    setScheme,
  } = useDisplayOptions(false, "default");

  /* schema & columns */
  const { schemaData } = useTableSchema(conn, table || null);
  const columns = useMemo(
    () => schemaData.map((c) => (c.name || c.NAME) as string),
    [schemaData],
  );

  /* declared duck types per column */
  const declaredTypes = useMemo(
    () =>
      Object.fromEntries(
        schemaData.map((c) => [
          (c.name || c.NAME) as string,
          (c.type_formatted || c.type || c.TYPE) as string,
        ]),
      ) as Record<string, string>,
    [schemaData],
  );

  /* filters */
  const {
    filters,
    dirty,
    joinOp,           // ← new
    setJoinOp,        // ← new
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    applyFilters,
    whereClause,
  } = useFilters(columns);

  /* query runner */
  const {
    data,
    cols,
    totalCount,
    rowCount,
    isPaginated,
    page,
    next,
    prev,
    error,
    run,
  } = useQueryRunner(conn, null, rowsPerPage);

  const debWhere = useDebounce(whereClause, 250);

  /* Compose SQL */
  const querySql = useMemo(
    () => (table ? `SELECT * FROM "${table}" ${debWhere}` : ""),
    [table, debWhere],
  );

  /* fetch data */
  useEffect(() => {
    if (!conn || !table) return;
    msgRef.current("Loading data…");
    run(querySql).then(() => {
      msgRef.current(error ? `Error: ${error.message}` : "Data loaded");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn, querySql, rowsPerPage, run]);

  /* CSV export */
  const downloadCsv = async () => {
    if (!conn || !table) return;
    const base = `SELECT * FROM "${table}" ${debWhere}`;
    const limited = applyLimit(base, limitsConfig.csvDownload.maxRows);
    const fname = `${csvName.trim() || table || "data"}.csv`;
    msgRef.current("Preparing CSV…");
    await downloadQueryToCsv(conn, limited, fname);
    msgRef.current(`CSV download: ${fname}`);
  };

  /* memoised grid */
  const grid = useMemo(
    () => (
      <DataTable
        data={data}
        columns={cols}
        declaredTypes={declaredTypes}
        visibleRows={20}
        improveColumnNames={improveNames}
        colorScheme={scheme}
        enableRowCopy
      />
    ),
    [data, cols, declaredTypes, improveNames, scheme],
  );

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="text-brand">🗂️</span> Table Browser
        </CardTitle>
        <CardDescription>
          Browse any registered table · live client-side filters
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-4">
        {/* ── controls row */}
        <div className="flex flex-wrap items-end gap-4">
          {/* table picker */}
          <div className="space-y-1.5 min-w-[240px] flex-1">
            <label className="text-sm font-medium">Table</label>
            <select
              className="w-full h-9 border rounded-md px-2 bg-background"
              value={table}
              disabled={!tables.length}
              onChange={(e) => {
                setTable(e.target.value);
                clearFilters();
              }}
            >
              <option value="" disabled>
                Select a table…
              </option>
              {tables.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* filter toggle */}
          {table && (
            <Button
              variant={filters.length ? "brand" : "outline"}
              size="icon"
              onClick={() => setShowFilterPanel((o) => !o)}
              title="Filters"
            >
              <FilterIcon className="h-4 w-4" />
            </Button>
          )}

          {/* display options */}
          <div className="ml-auto">
            <DisplayOptions
              open={optsOpen}
              toggle={toggleOpts}
              improveNames={improveNames}
              setImproveNames={setImproveNames}
              scheme={scheme}
              setScheme={setScheme}
            />
          </div>
        </div>

        {/* filters panel */}
        {showFilterPanel && (
          <FiltersPanel
            conn={conn}
            table={table}
            columns={columns}
            duckTypes={declaredTypes}   // ← new
            filters={filters}
            dirty={dirty}
            joinOp={joinOp}                 // ← new
            setJoinOp={setJoinOp}           // ← new
            add={addFilter}
            update={updateFilter}
            remove={removeFilter}
            clear={clearFilters}
            apply={applyFilters}
          />
        )}

        {/* data grid */}
        <div className="relative flex-1 flex flex-col overflow-hidden">
          {error ? (
            <div className="flex-1 flex items-center justify-center text-destructive">
              {error.message}
            </div>
          ) : data.length ? (
            <>
              {/* summary + controls */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <span className="text-sm font-medium">
                  Table rows: {totalCount.toLocaleString()} | Showing {rowCount} rows
                </span>

                <div className="flex items-center gap-2">
                  <label className="text-sm">Rows per page:</label>
                  <select
                    className="h-8 border rounded-md px-2 bg-background"
                    value={rowsPerPage}
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  >
                    {rowDisplayOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <Input
                    placeholder="file name"
                    value={csvName}
                    onChange={(e) => setCsvName(e.target.value)}
                    className="h-8 w-40"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!data.length}
                    onClick={downloadCsv}
                  >
                    <DownloadIcon className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>

              {grid}

              {isPaginated && (
                <Pager
                  page={page}
                  pageSize={rowsPerPage}
                  total={totalCount}
                  onPrev={prev}
                  onNext={next}
                />
              )}
            </>
          ) : table ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              No rows match these filters
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a table to view
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
